/**
 * Post-login return-destination validation (T023; FR-024, FR-033).
 *
 * After a successful SSO or legacy sign-in the app may need to send the user back to wherever
 * they originally tried to go — a same-origin path, typically carried in a query parameter or a
 * short-lived pre-auth continuation value. Because that value round-trips through an untrusted
 * redirect (the identity provider, a bookmarked link, a query string a user can hand-edit), it
 * must be treated as hostile input: spec.md's FR-033 threat model calls out "malicious return
 * destinations" explicitly, and the Edge Cases section requires rejecting a destination that is
 * "external, encoded more than once, malformed, stale, or no longer permitted".
 *
 * This module is a pure validator: given a raw candidate destination, it returns either that
 * candidate (once decoded) if — and only if — it is a well-formed same-origin relative path, or a
 * safe fallback otherwise. It has no knowledge of *where* the candidate came from and does not
 * track time or consume any one-time continuation state.
 *
 * ## Design decisions not fully spelled out by spec.md
 *
 * - **"Decode exactly once"**: The candidate is percent-decoded a single time. If the decoded
 *   result itself still contains what looks like a percent-encoded byte, we do NOT decode again —
 *   that second decode is exactly the double-encoding attack this function guards against (e.g.
 *   `%2568ttps://evil.com` decodes once to `%68ttps://evil.com`, which still hides `https:` behind
 *   `%68`; decoding a second time would reveal `https://evil.com`). Treating any residual
 *   percent-encoding as invalid, rather than resolving it, closes that hole without needing to
 *   reason about how many times a browser or proxy might decode a value.
 *
 * - **"No longer permitted" / "stale" (spec.md Edge Cases)**: Those two words describe the
 *   lifecycle of a *stored continuation value* (e.g. a pre-auth attempt record, or a one-time
 *   marker created before the SSO detour) — whether it has expired, been consumed already, or
 *   belongs to a different flow generation. That is a property of the callback/continuation
 *   *artifact*, not of the destination *string*, and it is explicitly T024's concern ("invalidate
 *   continuation material on every outcome"), not this task's. This function only ever sees a
 *   destination value and has no access to (and does not need) any timestamp, generation, or
 *   consumption state to do its job. Concretely: T024 is expected to decide *whether* a stored
 *   return destination is still eligible to be used at all (fresh, matching generation, not
 *   already consumed) and only pass an eligible value in here; this function then independently
 *   decides whether that value, taken at face value, is safe to navigate to. A destination that
 *   fails either check ends up at the same {@link DEFAULT_POST_LOGIN_DESTINATION} fallback.
 *
 * - **Fallback destination**: `/app/overview` is reused as the safe default post-login landing
 *   route — it is the same route the existing legacy login flow redirects to on success (see
 *   `src/modules/auth/e2e/login.test.ts`, which asserts a `waitForURL` against that path). No
 *   existing shared constant for it was found in `src/modules/auth/constants.ts`,
 *   so it is declared locally here and exported for reuse by later tasks (e.g. T024) rather than
 *   duplicated as a string literal.
 */

/** Safe default landing route once a requested return destination is absent, malformed, or hostile. */
export const DEFAULT_POST_LOGIN_DESTINATION = '/app/overview';

/** Matches any percent-encoded byte (e.g. `%68`, `%2F`) remaining after a single decode pass. */
const RESIDUAL_PERCENT_ENCODING_PATTERN = /%[0-9a-fA-F]{2}/;

/** Matches a leading URI scheme (`https:`, `javascript:`, `data:`, ...). */
const LEADING_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/** Highest character code (inclusive) treated as a disallowed ASCII control character. */
const MAX_LOW_CONTROL_CODE = 31;
/** The DEL control character code, also disallowed. */
const DEL_CONTROL_CODE = 127;

/**
 * Resolves an untrusted candidate post-login return destination to a safe, same-origin path.
 *
 * @param rawDestination The raw, still-encoded candidate destination (e.g. straight from a query
 *   parameter or a stored pre-auth value). Any non-string, empty, malformed, non-same-origin, or
 *   otherwise hostile input resolves to {@link DEFAULT_POST_LOGIN_DESTINATION}.
 * @returns The single-decoded destination when it is a valid same-origin relative path, otherwise
 *   {@link DEFAULT_POST_LOGIN_DESTINATION}.
 */
export function resolveReturnDestination(rawDestination: unknown): string {
  if (typeof rawDestination !== 'string' || rawDestination.length === 0) {
    return DEFAULT_POST_LOGIN_DESTINATION;
  }

  let decodedOnce: string;
  try {
    decodedOnce = decodeURIComponent(rawDestination);
  } catch {
    // Malformed percent-encoding (e.g. a lone `%` or an incomplete escape) — not a valid path.
    return DEFAULT_POST_LOGIN_DESTINATION;
  }

  if (RESIDUAL_PERCENT_ENCODING_PATTERN.test(decodedOnce)) {
    // Still looks percent-encoded after exactly one decode: could be hiding a scheme/host behind
    // a second decode. Reject rather than decode again.
    return DEFAULT_POST_LOGIN_DESTINATION;
  }

  return isSafeSameOriginPath(decodedOnce) ? decodedOnce : DEFAULT_POST_LOGIN_DESTINATION;
}

/**
 * True only for a well-formed, same-origin, root-relative path — never an absolute URL, a
 * protocol-relative URL, a pseudo-protocol, or a backslash-based origin-confusion trick.
 */
function isSafeSameOriginPath(candidate: string): boolean {
  // Browsers normalize backslashes to forward slashes for special schemes, so `/\evil.com` or
  // `\/evil.com` can turn into a protocol-relative navigation to another origin even though the
  // raw string "looks" rooted. Reject any backslash outright.
  if (candidate.includes('\\')) {
    return false;
  }

  // An embedded scheme anywhere (`https:`, `javascript:`, `data:`, ...) or a protocol-relative
  // prefix (`//evil.com`) is never a same-origin relative path.
  if (LEADING_SCHEME_PATTERN.test(candidate) || candidate.startsWith('//')) {
    return false;
  }

  // Must be rooted at the app origin: exactly one leading `/`, not `//...`.
  if (!candidate.startsWith('/')) {
    return false;
  }

  // Reject raw ASCII control characters (e.g. an embedded CR/LF), which have no legitimate place
  // in a same-origin path and can be used to smuggle a header/response split or a reinterpreted
  // URL past naive checks.
  for (let index = 0; index < candidate.length; index += 1) {
    const code = candidate.charCodeAt(index);
    if (code <= MAX_LOW_CONTROL_CODE || code === DEL_CONTROL_CODE) {
      return false;
    }
  }

  // Belt-and-suspenders: resolve against an arbitrary fixed base with the WHATWG URL parser and
  // confirm the result never leaves that base's origin. This catches any origin-confusion
  // encoding the checks above didn't anticipate.
  const base = 'https://return-destination.invalid';
  const resolved = parseAgainst(candidate, base);
  return resolved !== null && resolved.origin === base;
}

/** Resolves `candidate` against `base`, or `null` when it is not a parseable URL. */
function parseAgainst(candidate: string, base: string): URL | null {
  try {
    return new URL(candidate, base);
  } catch {
    return null;
  }
}

/** Where the latest sign-in landed, until {@link resolveUnmatchedRouteDestination} consumes it. */
let signInLandingPath: string | null = null;

/**
 * Records the path a sign-in just landed on (FR-024, T086). Called once per completed sign-in:
 * after an SSO callback, and before the legacy login navigation.
 */
export function markSignInLanding(destination: string): void {
  signInLandingPath = new URL(destination, window.location.origin).pathname;
}

/**
 * The target for a protected route the router could not match. The signed-in user's permissions
 * decide which routes are registered, so an unmatched sign-in landing is a destination this user
 * may not open: it falls back to the default dashboard once. Any other unmatched path keeps going
 * to `/error`, as before.
 */
export function resolveUnmatchedRouteDestination(pathname: string): string {
  if (signInLandingPath !== null && signInLandingPath === pathname) {
    signInLandingPath = null;
    return DEFAULT_POST_LOGIN_DESTINATION;
  }
  return '/error';
}

export function resetSignInLandingForTesting(): void {
  signInLandingPath = null;
}
